/** /agravos/sifilis (D-060): gestações com sífilis e a situação de cada etapa da linha de cuidado. */
import { ROTULO_CLASSIFICACAO_NEONATAL } from '@metaepi/shared';
import { useState } from 'react';
import { Link } from 'react-router';
import {
  CabecalhoPagina,
  Carregando,
  ConteudoPagina,
  EstadoErro,
  EstadoVazio,
  Selecao,
  SeloStatus,
  Tabela,
  type TipoStatus,
} from '../../components/ui';
import { mensagemDeErro } from '../../lib/erros';
import {
  FILTRO_FASE,
  useAgravoSifilis,
  type ItemSifilis,
  type ParceiroAgravo,
  type TratamentoAgravo,
} from './api';
import { CelulaFase, CelulaGestante, Contador, GradeContadores, SeloNotificacao } from './comum';

const TRATAMENTO: Record<TratamentoAgravo, { texto: string; tipo: TipoStatus }> = {
  concluido: { texto: 'Concluído', tipo: 'sucesso' },
  em_dia: { texto: 'Em dia', tipo: 'info' },
  dose_pendente: { texto: 'Dose pendente', tipo: 'alerta' },
  reiniciar: { texto: 'Reiniciar', tipo: 'perigo' },
  nao_iniciado: { texto: 'Não iniciado', tipo: 'perigo' },
};

const PARCEIRO: Record<ParceiroAgravo, { texto: string; tipo: TipoStatus }> = {
  tratado: { texto: 'Tratado', tipo: 'sucesso' },
  em_tratamento: { texto: 'Em tratamento', tipo: 'info' },
  pendente: { texto: 'Sem tratamento', tipo: 'alerta' },
  nao_tratado: { texto: 'Não tratado (motivo registrado)', tipo: 'alerta' },
  nao_cadastrado: { texto: 'Não cadastrado', tipo: 'alerta' },
};

const SITUACOES = [
  { valor: '', rotulo: 'Todas as situações' },
  { valor: 'tratamento', rotulo: 'Tratamento precisa de atenção' },
  { valor: 'parceiro', rotulo: 'Parceiro pendente' },
  { valor: 'notificacao', rotulo: 'Notificação pendente' },
  { valor: 'seguimento', rotulo: 'Criança em seguimento' },
];

function confere(i: ItemSifilis, situacao: string) {
  switch (situacao) {
    case 'tratamento':
      return ['dose_pendente', 'reiniciar', 'nao_iniciado'].includes(i.tratamento);
    case 'parceiro':
      return ['nao_cadastrado', 'pendente', 'nao_tratado'].includes(i.parceiro);
    case 'notificacao':
      return i.notificacao !== 'pdf_gerado' && i.notificacao !== 'notificada';
    case 'seguimento':
      return i.recemNascidos.some((r) => r.seguimento === 'em_seguimento');
    default:
      return true;
  }
}

function CelulaCrianca({ item }: { item: ItemSifilis }) {
  if (item.recemNascidos.length === 0) return <span className="text-texto-suave">—</span>;
  return (
    <ul className="flex flex-col gap-1">
      {item.recemNascidos.map((r) => (
        <li key={r.id} className="flex flex-col">
          <Link
            to={r.seguimento_id ? `/seguimentos/${r.seguimento_id}` : `/recem-nascidos/${r.id}`}
            className="font-medium text-primaria hover:underline"
          >
            {r.nome ?? 'Recém-nascido'}
          </Link>
          <span className="text-xs text-texto-suave">
            {r.seguimento === 'em_seguimento'
              ? 'Em seguimento'
              : r.seguimento === 'encerrado'
                ? 'Seguimento encerrado'
                : r.classificacao
                  ? ROTULO_CLASSIFICACAO_NEONATAL[r.classificacao]
                  : r.tipo_concepto === 'nascido_vivo'
                    ? 'Conduta a confirmar'
                    : 'Natimorto ou aborto'}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function PaginaSifilis() {
  const pagina = useAgravoSifilis();
  const [fase, setFase] = useState('');
  const [situacao, setSituacao] = useState('');
  const itens = (pagina.data?.itens ?? []).filter(
    (i) => (!fase || i.fase === fase) && confere(i, situacao),
  );
  const c = pagina.data?.contadores;

  return (
    <ConteudoPagina>
      <CabecalhoPagina
        ajuda="agenda#agravos"
        titulo="Sífilis"
        descricao="Gestantes com sífilis no seu município, do pré-natal ao seguimento da criança. Abra a ficha para ver os detalhes."
      />
      {pagina.isPending ? (
        <Carregando linhas={5} />
      ) : pagina.isError ? (
        <EstadoErro
          descricao={mensagemDeErro(pagina.error)}
          aoTentarNovamente={() => void pagina.refetch()}
        />
      ) : pagina.data.precisaMunicipio || !c ? (
        <EstadoVazio
          titulo="Seu usuário não está vinculado a um município."
          descricao="Peça ao administrador para vincular o seu usuário a um município."
        />
      ) : (
        <>
          <GradeContadores>
            <Contador rotulo="Gestantes com sífilis" valor={c.gestantes} />
            <Contador rotulo="Tratamento concluído" valor={c.tratamentoConcluido} />
            <Contador rotulo="Tratamento em dia" valor={c.tratamentoEmDia} />
            <Contador
              rotulo="Tratamento precisa de atenção"
              valor={c.tratamentoAtencao}
              tipo="perigo"
            />
            <Contador rotulo="Parceiro pendente" valor={c.parceiroPendente} tipo="alerta" />
            <Contador rotulo="Notificação pendente" valor={c.notificacaoPendente} tipo="alerta" />
            <Contador rotulo="Crianças em seguimento" valor={c.criancasEmSeguimento} />
          </GradeContadores>
          <div className="grid gap-4 sm:grid-cols-2">
            <Selecao
              rotulo="Fase"
              value={fase}
              onChange={(e) => setFase(e.target.value)}
              opcoes={FILTRO_FASE}
            />
            <Selecao
              rotulo="Situação"
              value={situacao}
              onChange={(e) => setSituacao(e.target.value)}
              opcoes={SITUACOES}
            />
          </div>
          {itens.length === 0 ? (
            <EstadoVazio
              titulo={
                fase || situacao
                  ? 'Nenhuma gestante com estes filtros.'
                  : 'Nenhuma gestante com sífilis em acompanhamento.'
              }
            />
          ) : (
            <>
              <p className="text-sm text-texto-suave">{`${itens.length} ${itens.length === 1 ? 'gestante' : 'gestantes'}.`}</p>
              <Tabela
                legenda="Gestantes com sífilis"
                itens={itens}
                chave={(i) => i.id}
                colunas={[
                  {
                    titulo: 'Gestante',
                    celula: (i: ItemSifilis) => <CelulaGestante item={i} aba="tratamento" />,
                  },
                  {
                    titulo: 'Fase',
                    celula: (i: ItemSifilis) => (
                      <CelulaFase fase={i.fase} dum={i.data_ultima_menstruacao} />
                    ),
                  },
                  {
                    titulo: 'Tratamento',
                    celula: (i: ItemSifilis) => (
                      <SeloStatus tipo={TRATAMENTO[i.tratamento].tipo}>
                        {TRATAMENTO[i.tratamento].texto}
                      </SeloStatus>
                    ),
                  },
                  {
                    titulo: 'Parceiro',
                    celula: (i: ItemSifilis) => (
                      <SeloStatus tipo={PARCEIRO[i.parceiro].tipo}>
                        {PARCEIRO[i.parceiro].texto}
                      </SeloStatus>
                    ),
                  },
                  {
                    titulo: 'Notificação',
                    celula: (i: ItemSifilis) => <SeloNotificacao status={i.notificacao} />,
                  },
                  { titulo: 'Criança', celula: (i: ItemSifilis) => <CelulaCrianca item={i} /> },
                  {
                    titulo: 'Unidade',
                    celula: (i: ItemSifilis) => i.unidade_nome,
                    secundaria: true,
                  },
                ]}
              />
            </>
          )}
        </>
      )}
    </ConteudoPagina>
  );
}
